import React, { useState, useEffect, Fragment } from "react";
import { useContacts } from "hooks/useContacts";
import { assignContactToLead } from "services/contactServices";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import AssignContactsToLeadModal from "./AssignContactsToLeadModal";
import ContactModal from "app/pages/settings/sections/ContactsManagement/ContactModal";
import { ConfirmModal } from "components/shared/ConfirmModal";
import ViewContactModal from "./ViewContactModal";
import { formatABN } from "utils/formatABN";
import { invalidateTimelineCache } from "hooks/useTimeline";
import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BriefcaseIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { toast } from "sonner";
import { formatNumber } from "utils/formatNumber";
import { canEditLead } from "permissions/permissionHelpers";

const getInitials = (name) => {
  if (!name || typeof name !== "string") return "U";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "U";
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
};

export default function ContactsTab({ leadId, leadData, onRefreshParent }) {
  const [contactsSearch, setContactsSearch] = useState("");
  const [debouncedContactsSearch, setDebouncedContactsSearch] = useState("");
  const [isAssignContactModalOpen, setIsAssignContactModalOpen] =
    useState(false);
  const [contactModalMode, setContactModalMode] = useState("new");
  const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false);
  const [selectedContactForEdit, setSelectedContactForEdit] = useState(null);
  const [isDeassignConfirmOpen, setIsDeassignConfirmOpen] = useState(false);
  const [selectedContactForDeassign, setSelectedContactForDeassign] =
    useState(null);
  const [isDeassigning, setIsDeassigning] = useState(false);
  const [isViewContactModalOpen, setIsViewContactModalOpen] = useState(false);
  const [selectedContactForView, setSelectedContactForView] = useState(null);

  useEffect(() => {
    const trimmed = contactsSearch.trim();
    if (trimmed.length > 0 && trimmed.length < 3) {
      setDebouncedContactsSearch("");
      return;
    }
    const handler = setTimeout(() => {
      setDebouncedContactsSearch(trimmed);
    }, 300);
    return () => clearTimeout(handler);
  }, [contactsSearch]);

  const {
    contacts: assignedContacts,
    isLoading: isContactsLoading,
    refresh: refreshContacts,
  } = useContacts(leadId, debouncedContactsSearch);
  const isSearchPending =
    contactsSearch.trim().length >= 3 && contactsSearch !== debouncedContactsSearch;
  const isCurrentlySearchingContacts = isContactsLoading || isSearchPending;

  const handleEditContactClick = (contact) => {
    setSelectedContactForEdit(contact);
    setIsEditContactModalOpen(true);
  };

  const handleDeassignClick = (contact) => {
    setSelectedContactForDeassign(contact);
    setIsDeassignConfirmOpen(true);
  };

  const handleDeassignConfirm = async () => {
    if (!selectedContactForDeassign || !leadId) return;
    setIsDeassigning(true);
    try {
      const currentAssignedIds = (assignedContacts || [])
        .map((c) => c.id || c.ContactId || c.Id)
        .filter(
          (id) =>
            id !==
            (selectedContactForDeassign.id ||
              selectedContactForDeassign.ContactId ||
              selectedContactForDeassign.Id),
        );

      const res = await assignContactToLead(leadId, currentAssignedIds);
      if (res.success) {
        toast.success("Contact deassigned successfully");
        refreshContacts();
        if (onRefreshParent) onRefreshParent();
        invalidateTimelineCache(leadId);
      } else {
        toast.error(res.error || "Failed to deassign contact");
      }
    } catch (error) {
      console.error("Error deassigning contact:", error);
      toast.error("Failed to deassign contact");
    } finally {
      setIsDeassigning(false);
      setIsDeassignConfirmOpen(false);
      setSelectedContactForDeassign(null);
    }
  };

  const leadName =
    leadData?.FullName ||
    leadData?.name ||
    leadData?.LeadName ||
    "Unknown User";
  const phoneNumber = formatNumber(leadData?.PhoneNumber) || "N/A";
  const emailAddress = leadData?.EmailId || leadData?.email || "N/A";

  const assignLeadDetails = leadData
    ? {
        fullName: leadName === "Unknown User" ? "" : leadName,
        email: emailAddress === "N/A" ? "" : emailAddress,
        phoneNumber: phoneNumber === "N/A" ? "" : phoneNumber,
        ...leadData,
      }
    : null;

  return (
    <div className="space-y-4 py-2">
      {/* Search + Add button */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon
              className="h-4 w-4 text-gray-400"
              aria-hidden="true"
            />
          </div>
          <input
            type="text"
            value={contactsSearch}
            onChange={(e) => setContactsSearch(e.target.value)}
            className="dark:border-dark-600 dark:bg-dark-900/50 focus:border-primary-500 focus:ring-primary-500 block w-full rounded-lg border border-gray-300 bg-gray-50 py-1 pr-3 pl-10 text-sm text-gray-900 transition-all placeholder:text-gray-500 focus:ring-1 focus:outline-none dark:text-white"
            placeholder="Search contacts..."
          />
        </div>

        {canEditLead() && (
          <Popover className="relative">
            <PopoverButton className="bg-primary-600 hover:bg-primary-700 focus-visible:outline-primary-600 inline-flex cursor-pointer items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">
              <PlusIcon className="h-4 w-4" aria-hidden="true" />
              Contact
            </PopoverButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel className="dark:bg-dark-800 absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
                {({ close }) => (
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setContactModalMode("new");
                        setIsAssignContactModalOpen(true);
                        close();
                      }}
                      className="dark:hover:bg-dark-700 block w-full px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300"
                    >
                      New Contact
                    </button>
                    <button
                      onClick={() => {
                        setContactModalMode("existing");
                        setIsAssignContactModalOpen(true);
                        close();
                      }}
                      className="dark:hover:bg-dark-700 block w-full px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300"
                    >
                      Existing Contact
                    </button>
                  </div>
                )}
              </PopoverPanel>
            </Transition>
          </Popover>
        )}
      </div>

      {/* Contacts list */}
      <div className="dark:border-dark-700 dark:bg-dark-800 overflow-hidden rounded-xl border border-gray-200 bg-white">
        {isCurrentlySearchingContacts ? (
          <div className="flex items-center justify-center p-8">
            <div className="border-primary-500 h-6 w-6 animate-spin rounded-full border-2 border-t-transparent"></div>
          </div>
        ) : assignedContacts?.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center text-gray-500">
            <UserGroupIcon className="mb-2 h-10 w-10 opacity-20" />
            <p className="text-sm font-medium">No contacts found</p>
            {contactsSearch && (
              <p className="mt-1 text-xs text-gray-400">
                Try adjusting your search
              </p>
            )}
          </div>
        ) : (
          <ul className="dark:divide-dark-700 divide-y divide-gray-100">
            {assignedContacts?.map((contact) => {
              const street = contact.StreetAddress;
              const suburb = contact.Suburb;
              const city = contact.City;
              const state = contact.State;
              const postalCode = contact.PostalCode;
              const address = [street, suburb, city, postalCode, state]
                .filter(Boolean)
                .join(", ");
              const phone = contact.PhoneNumber;
              const email = contact.Email;
              const abn = contact.ABNNumber;
              const companyName = contact.CompanyName;

              return (
                <li
                  key={contact.id || contact.ContactId || contact.Id}
                  onClick={() => {
                    setSelectedContactForView(contact);
                    setIsViewContactModalOpen(true);
                  }}
                  className="group dark:hover:bg-dark-700/40 flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-gray-50"
                >
                  {/* Avatar */}
                  <div className="bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 ring-primary-100 dark:ring-primary-900/30 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold ring-1">
                    {getInitials(contact.FullName)}
                  </div>

                  {/* Contact details */}
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <p className="truncate text-xs leading-tight font-semibold text-gray-900 dark:text-white">
                      {contact.FullName || "Unknown"}
                    </p>
                    {companyName && (
                      <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                        <BriefcaseIcon className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
                        <span className="truncate">{companyName}</span>
                      </div>
                    )}
                    {phone && (
                      <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                        <PhoneIcon className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
                        <span className="truncate">
                          {formatNumber(phone) || phone}
                        </span>
                      </div>
                    )}
                    {email && (
                      <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                        <EnvelopeIcon className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
                        <span className="truncate">{email}</span>
                      </div>
                    )}
                    {address && (
                      <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                        <MapPinIcon className="text-primary-400 dark:text-primary-600 h-3 w-3 shrink-0" />
                        <span className="truncate" title={address}>
                          {address}
                        </span>
                      </div>
                    )}
                    {abn ? (
                      <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                        <DocumentTextIcon className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
                        <span className="truncate">ABN: {formatABN(abn)}</span>
                      </div>
                    ): null}
                  </div>

                  {/* Action buttons */}
                  {canEditLead() && (
                    <div
                      className="flex shrink-0 items-center gap-0.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditContactClick(contact);
                        }}
                        className="hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-gray-400 transition-colors"
                        title="Edit Contact"
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeassignClick(contact);
                        }}
                        className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                        title="Remove Contact"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <AssignContactsToLeadModal
        isOpen={isAssignContactModalOpen}
        onClose={() => setIsAssignContactModalOpen(false)}
        leadId={leadId}
        initialMode={contactModalMode}
        onAssigned={() => {
          refreshContacts();
          if (onRefreshParent) onRefreshParent();
          invalidateTimelineCache(leadId);
          setIsAssignContactModalOpen(false);
        }}
        hasAssignedContacts={(assignedContacts?.length || 0) > 0}
        leadDetails={assignLeadDetails}
      />

      <ContactModal
        isOpen={isEditContactModalOpen}
        onClose={() => {
          setIsEditContactModalOpen(false);
          setSelectedContactForEdit(null);
        }}
        contactData={selectedContactForEdit}
        onSuccess={() => {
          refreshContacts();
          if (onRefreshParent) onRefreshParent();
          invalidateTimelineCache(leadId);
        }}
      />

      <ViewContactModal
        isOpen={isViewContactModalOpen}
        onClose={() => {
          setIsViewContactModalOpen(false);
          setSelectedContactForView(null);
        }}
        contact={selectedContactForView}
        onEdit={canEditLead() ? handleEditContactClick : undefined}
      />

      <ConfirmModal
        show={isDeassignConfirmOpen}
        state="pending"
        confirmLoading={isDeassigning}
        onClose={() => {
          setIsDeassignConfirmOpen(false);
          setSelectedContactForDeassign(null);
        }}
        onOk={handleDeassignConfirm}
        messages={{
          pending: {
            title: "Deassign Contact?",
            description: `Are you sure you want to remove ${selectedContactForDeassign?.FullName || selectedContactForDeassign?.fullName || "this contact"} from this lead?`,
            actionText: "Deassign",
          },
          success: {
            title: "Contact Deassigned",
            description: "The contact has been removed from this lead.",
          },
        }}
        buttonColor="error"
      />
    </div>
  );
}
